// Renders the student's uploaded worksheet pages with each question's detected
// solution region(s) drawn on top — the visual QA surface for the solution-split
// bounding boxes. Boxes are Gemini-native box2d [ymin, xmin, ymax, xmax] on a
// 0–1000 grid normalized to the page, so they map to the displayed image as pure
// CSS percentages (÷10) regardless of the image's pixel resolution.
import { useMemo, useState } from 'react';
import type { PersistedSplitSolution } from './solutionSplitterTypes';

// Distinct, high-contrast hues cycled by question index. Kept local so this
// component has no dependency on a shared chart palette.
const PALETTE = [
    '#3b82f6', '#16a34a', '#d97706', '#ec4899', '#8b5cf6',
    '#14b8a6', '#ef4444', '#eab308', '#0ea5e9', '#a855f7',
];
const colorFor = (questionIndex: number) => PALETTE[questionIndex % PALETTE.length];

type BoxMode = 'refined' | 'raw' | 'both';

interface BoxOnPage {
    questionIndex: number;
    splitConfidence: number;
    /** Refined band [ymin, xmin, ymax, xmax] on 0–1000. */
    refined?: [number, number, number, number];
    /** Raw model box [ymin, xmin, ymax, xmax] on 0–1000. */
    raw?: [number, number, number, number];
}

export function SolutionSplitPages({
    pageUrls,
    splitSolutions,
}: {
    pageUrls: string[];
    splitSolutions: PersistedSplitSolution[];
}) {
    const [showBoxes, setShowBoxes] = useState(true);
    // Which regions to draw: the deterministic refined bands (default), the raw
    // model boxes, or both overlaid (refined solid + raw dashed) for comparison.
    const [mode, setMode] = useState<BoxMode>('refined');
    // null = show every question's boxes; otherwise isolate one question.
    const [activeQuestion, setActiveQuestion] = useState<number | null>(null);

    // page index → boxes drawn on that page. Raw and refined rects are aligned
    // 1:1 by index (the backend derives refined from raw), so we zip them.
    const boxesByPage = useMemo(() => {
        const map = new Map<number, BoxOnPage[]>();
        for (const s of splitSolutions) {
            const rects = s.boundingRects || [];
            rects.forEach((r, i) => {
                const list = map.get(r.pageIndex) || [];
                list.push({
                    questionIndex: s.questionIndex,
                    splitConfidence: s.splitConfidence,
                    raw: r.box2d,
                    refined: s.refinedBoundingRects?.[i]?.box2d,
                });
                map.set(r.pageIndex, list);
            });
        }
        return map;
    }, [splitSolutions]);

    // Questions that produced at least one box (in question order), for the legend.
    const boxedQuestions = useMemo(
        () => splitSolutions
            .filter(s => (s.boundingRects || []).length > 0)
            .sort((a, b) => a.questionIndex - b.questionIndex),
        [splitSolutions],
    );

    // Attempted questions with NO box — the failure mode worth flagging.
    const attemptedNoBox = useMemo(
        () => splitSolutions.filter(s => s.attempted && (s.boundingRects || []).length === 0),
        [splitSolutions],
    );

    const totalBoxes = useMemo(
        () => splitSolutions.reduce((n, s) => n + (s.boundingRects?.length || 0), 0),
        [splitSolutions],
    );

    if (pageUrls.length === 0) return null;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={showBoxes} onChange={e => setShowBoxes(e.target.checked)} />
                    Show boxes ({totalBoxes})
                </label>
                {showBoxes && (
                    <div style={{ display: 'inline-flex', border: '1px solid var(--border-color, #2a2a2a)', borderRadius: 6, overflow: 'hidden' }}>
                        {(['refined', 'raw', 'both'] as BoxMode[]).map(m => (
                            <button
                                key={m}
                                onClick={() => setMode(m)}
                                title={m === 'refined' ? 'Deterministic full-width bands (what will be cropped)' : m === 'raw' ? 'Raw model boxes' : 'Refined (solid) over raw (dashed)'}
                                style={{
                                    fontSize: 12, padding: '3px 10px', cursor: 'pointer', border: 'none',
                                    background: mode === m ? 'var(--color-info)' : 'transparent',
                                    color: mode === m ? '#fff' : 'var(--text-secondary)',
                                    fontWeight: mode === m ? 700 : 500,
                                }}
                            >
                                {m === 'refined' ? 'Refined' : m === 'raw' ? 'Raw' : 'Both'}
                            </button>
                        ))}
                    </div>
                )}
                {boxedQuestions.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Isolate:</span>
                        {boxedQuestions.map(s => {
                            const active = activeQuestion === s.questionIndex;
                            const c = colorFor(s.questionIndex);
                            return (
                                <button
                                    key={s.questionIndex}
                                    onClick={() => setActiveQuestion(active ? null : s.questionIndex)}
                                    title={`split confidence ${Math.round(s.splitConfidence * 100)}%`}
                                    style={{
                                        fontSize: 12, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                                        border: `1.5px solid ${c}`,
                                        background: active ? c : 'transparent',
                                        color: active ? '#fff' : c,
                                        fontWeight: active ? 700 : 500,
                                    }}
                                >
                                    Q{s.questionIndex + 1} · {s.boundingRects.length}
                                </button>
                            );
                        })}
                        {activeQuestion !== null && (
                            <button
                                onClick={() => setActiveQuestion(null)}
                                style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, cursor: 'pointer', border: '1px solid var(--border-color, #2a2a2a)', background: 'transparent', color: 'var(--text-secondary)' }}
                            >
                                clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            {attemptedNoBox.length > 0 && (
                <p style={{ fontSize: 12, color: 'var(--color-warning)', margin: '0 0 10px' }}>
                    ⚠ {attemptedNoBox.length} attempted question(s) returned no box:{' '}
                    {attemptedNoBox.map(s => `Q${s.questionIndex + 1}`).join(', ')}
                </p>
            )}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {pageUrls.map((url, pageIndex) => {
                    const boxes = (boxesByPage.get(pageIndex) || [])
                        .filter(b => activeQuestion === null || b.questionIndex === activeQuestion);
                    return (
                        <figure key={pageIndex} style={{ margin: 0, width: 'min(100%, 440px)' }}>
                            <div style={{ position: 'relative', display: 'block', lineHeight: 0 }}>
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                    <img
                                        src={url}
                                        alt={`page ${pageIndex + 1}`}
                                        style={{ width: '100%', display: 'block', borderRadius: 4, border: '1px solid var(--border-color, #2a2a2a)' }}
                                    />
                                </a>
                                {showBoxes && boxes.map((b, i) => {
                                    const c = colorFor(b.questionIndex);
                                    const dim = activeQuestion !== null && b.questionIndex !== activeQuestion;
                                    const nodes = [];
                                    // Raw box (dashed) — shown in 'raw' and 'both'.
                                    if ((mode === 'raw' || mode === 'both') && b.raw) {
                                        const [ymin, xmin, ymax, xmax] = b.raw;
                                        nodes.push(
                                            <div key={`raw-${i}`} style={{
                                                position: 'absolute',
                                                left: `${xmin / 10}%`, top: `${ymin / 10}%`,
                                                width: `${(xmax - xmin) / 10}%`, height: `${(ymax - ymin) / 10}%`,
                                                border: `2px dashed ${mode === 'both' ? '#ef4444' : c}`,
                                                background: mode === 'both' ? 'transparent' : `${c}18`,
                                                boxSizing: 'border-box', borderRadius: 2,
                                                opacity: dim ? 0.15 : 1, pointerEvents: 'none',
                                            }} />
                                        );
                                    }
                                    // Refined band (solid) — shown in 'refined' and 'both'.
                                    const refined = b.refined ?? b.raw;
                                    if ((mode === 'refined' || mode === 'both') && refined) {
                                        const [ymin, xmin, ymax, xmax] = refined;
                                        nodes.push(
                                            <div key={`ref-${i}`} style={{
                                                position: 'absolute',
                                                left: `${xmin / 10}%`, top: `${ymin / 10}%`,
                                                width: `${(xmax - xmin) / 10}%`, height: `${(ymax - ymin) / 10}%`,
                                                border: `2px solid ${c}`,
                                                background: `${c}22`,
                                                boxSizing: 'border-box', borderRadius: 2,
                                                opacity: dim ? 0.15 : 1, pointerEvents: 'none',
                                            }}>
                                                <span style={{
                                                    position: 'absolute', top: 0, left: 0,
                                                    transform: 'translateY(-100%)',
                                                    background: c, color: '#fff',
                                                    fontSize: 10, lineHeight: '14px', padding: '0 4px',
                                                    borderRadius: '2px 2px 0 0', whiteSpace: 'nowrap',
                                                }}>
                                                    Q{b.questionIndex + 1} · {Math.round(b.splitConfidence * 100)}%
                                                </span>
                                            </div>
                                        );
                                    }
                                    return nodes;
                                })}
                            </div>
                            <figcaption style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, lineHeight: 1.4 }}>
                                page {pageIndex + 1}
                                {boxes.length > 0 && ` · ${boxes.length} box${boxes.length > 1 ? 'es' : ''}`}
                            </figcaption>
                        </figure>
                    );
                })}
            </div>
        </div>
    );
}
